'use strict';

var formValidation = require('../components/formValidation');
var clientSideValidation = require('../components/clientSideValidation');
var utils = require('brand_core/components/utils');

const virtualCardFields = ['giftCardRecipientEmail', 'giftCardFromName', 'giftCardRecipientName', 'giftMessage', 'giftCardSendDate', 'giftCardCustomAmount'];
const physicalCardFields = ['giftCardFromName', 'giftCardRecipientName', 'giftMessage', 'giftCardCustomAmount'];

/**
 * Get the summary validation status for the provided input fields
 * @param {selector} $container product details container
 * @param {*} fields array contains the field ids to check
 * @return {boolean} true in case if all fields are valid
 */
function getFieldsValidationStatus($container, fields) {
    var validationStatus = true;

    for (var fieldName of fields) { // eslint-disable-line
        var $field = $container.find('#' + fieldName);
        if ($field.length) {
            validationStatus = validationStatus && $field[0].validity.valid;
        }
    }
    return validationStatus;
}

/**
 * Get the object contains names and values of all provided input fields
 * @param {selector} $container product details container
 * @param {*} fields array contains the field ids
 * @return {Object} object contains pairs of field names and its values
 */
function getFieldsData($container, fields) {
    var fieldsData = {};

    for (var idx in fields) { // eslint-disable-line
        if (typeof fields[idx] === 'string') {
            var selector = $container.find('#' + fields[idx]);
            fieldsData[selector.attr('name')] = selector.val();
        }
    }
    return fieldsData;
}

/**
 * Returns selected Gift Card type
 * @param {selector} $container product details container
 * @returns {string} Gift card type (virtual|physical)
 */
function getSelectedGiftCardType($container) {
    return $container.find('[data-attr="giftCardTypeVariation"] button.selected span').data('attr-value');
}

/**
 * Updates the attributes section class according to the selected card type
 * @param {selector} $container jQuery selector which points to the gift card custom attributes form
 */
function updateCardAttributesFormClass($container) {
    var selectedType = getSelectedGiftCardType($container);
    var $attributesFormContainer = $container.find('.js-gc-attributes');
    $attributesFormContainer.find('form').attr('data-dtm-name', selectedType + ' gift card form');
    $attributesFormContainer.find('form').data('dtm-name', selectedType + ' gift card form');

    $container.find('.js-giftcard-icon').addClass('d-none');

    var designType = $container.find('[data-attr="giftCardDesignVariation"] button.selected span').data('attr-value');
    $container.find('[data-attr="giftCardDesignVariation"] label span')
        .text(designType);

    if (selectedType === 'virtual') {
        $attributesFormContainer.addClass('gc-virtual');
        $attributesFormContainer.removeClass('gc-physical');
        $container.find('.gc-physical-hidden').show();
        $container.find('.gc-virtual-hidden').hide();
        $container.find('.js-giftcard-email').removeClass('d-none');
    } else {
        $attributesFormContainer.addClass('gc-physical');
        $attributesFormContainer.removeClass('gc-virtual');
        $container.find('.gc-physical-hidden').hide();
        $container.find('.gc-virtual-hidden').show();
        $container.find('.js-giftcard-delivery').removeClass('d-none');
    }
}

/**
 * Process attribute values associated with an attribute that does not have image swatches
 *
 * @param {Object} attr - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {Object[]} attr.values - Array of attribute value objects
 * @param {string} attr.values.value - Attribute coded value
 * @param {string} attr.values.url - URL to de/select an attribute value of the product
 * @param {boolean} attr.values.isSelectable - Flag as to whether an attribute value can be
 *     selected.  If there is no variant that corresponds to a specific combination of attribute
 *     values, an attribute may be disabled in the Product Detail Page
 * @param {jQuery} $productContainer - DOM container for a given product
 */
function processValues(attr, $productContainer) {
    var $attr = '[data-attr="' + attr.id + '"]';

    attr.values.forEach(function (attrValue, idx) {
        var isTypePhysicalSwatch = attr.attributeId === 'giftCardTypeVariation' && attrValue.value === 'physical';
        var isCustomValueSwatch = attr.attributeId === 'giftCardValueVariation' && attrValue.value === 'CUSTOM';

        var $attrValue = $productContainer.find($attr + ' [data-attr-value="' + attrValue.value + '"]');
        var $swatchAnchor = $attrValue.parent();

        if (isCustomValueSwatch) {
            if (!attrValue.selected) {
                $('[name$="_giftCardCustomAmount"]').val('');
            }

            $swatchAnchor = $attrValue;
        }

        if (!isTypePhysicalSwatch) {
            if (attrValue.url) {
                $swatchAnchor.data('attr-url', attrValue.url);
            } else {
                $swatchAnchor.data('attr-url', '');
            }
        }

        $attrValue.removeClass('disabled selectable unselectable');
        $swatchAnchor.removeClass('disabled selectable unselectable');

        if (!attrValue.selectable && !isTypePhysicalSwatch) {
            $swatchAnchor.addClass('disabled unselectable');
            $attrValue.addClass('disabled unselectable');
        } else {
            $swatchAnchor.addClass('selectable');
            $attrValue.addClass('selectable');
        }

        if (attrValue.selected) {
            $attrValue.addClass('selected');
            $swatchAnchor.addClass('selected');
        } else {
            $attrValue.removeClass('selected');
            $swatchAnchor.removeClass('selected');
        }

        if (attrValue.images) {
            var swatch = (attrValue.images || {}).swatch || [];
            $productContainer.find($attr + ' img').eq(idx)
                .attr('src', swatch[0] ? swatch[0].url : '');
        }
    });
}

/**
 * Parses JSON from Ajax call made whenever an attribute value is [de]selected
 * @param {Object} response - response from Ajax call
 * @param {Object} response.product - Product object
 * @param {string} response.product.id - Product ID
 * @param {Object[]} response.product.variationAttributes - Product attributes
 * @param {Object[]} response.product.images - Product images
 * @param {boolean} response.product.hasRequiredAttrsSelected - Flag as to whether all required
 *     attributes have been selected.  Used partially to
 *     determine whether the Add to Cart button can be enabled
 * @param {jQuery} $productContainer - DOM element for a given product.
 */
function handleVariantResponse(response, $productContainer) {
    if (response.product.variationAttributes) {
        var attrs = response.product.variationAttributes;

        var isLargeImages = (response.product.images.large || {}).length;
        if (isLargeImages) {
            $productContainer.find('.primary-images img')
                .attr('src', response.product.images.large[0].url);
        }

        attrs.forEach(function (attr) {
            processValues(attr, $productContainer);
        });
    }
}

/**
 * Updates the digitalData object.
 * @param {Object} data The data to set in the digitalData object
 */
const updateDTM = (data) => {
    if (!!data && 'dtmLayer' in data && 'digitalData' in window) {
        const propsToUpdate = ['page', 'user', 'cart'];
        propsToUpdate.forEach((prop) => {
            if (data.dtmLayer[prop]) {
                window.digitalData[prop] = data.dtmLayer[prop];
            }
        });
    }
};

/**
 * updates the product view when a product attribute is selected or deselected
 *
 * @param {string} selectedValueUrl - the Url for the selected variation value
 * @param {jQuery} $productContainer - DOM element for current product
 */
function attributeSelect(selectedValueUrl, $productContainer) {
    if (selectedValueUrl) {
        $.ajax({
            url: selectedValueUrl,
            method: 'GET',
            success: function (data) {
                handleVariantResponse(data, $productContainer);
                $('body').trigger('product:afterAttributeSelect',
                    { data: data, container: $productContainer });
                updateDTM(data);
                $.spinner().stop();
            },
            error: function () {
                $.spinner().stop();
            }
        });
    }
}

/**
 * setup datepicker plugin
 */
function setupDatepicker() {
    var $dpInput = $('.gcdetails .js-gift-card-datepicker');

    if (!$dpInput.length) {
        return;
    }

    /**
    * hide datepicker
    */
    function closeDP() {
        $dpInput.datepicker('hide');
        $dpInput.blur();
    }

    /**
    * format desiredDate mm/dd/yyyy
    * @param {date} desiredDate - desiredDate
    */
    function setDatePickerDate(desiredDate) {
        $dpInput.val(desiredDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }));
    }

    /**
    * if date less than today set to today
    */
    function ensurePickerHasCurrentOrFutureDate() {
        var todaysDate = new Date();
        if ($dpInput.val() && Date.parse($dpInput.val())) {
            var inputDate = new Date($dpInput.val());
            if (inputDate.setHours(0, 0, 0, 0) < todaysDate.setHours(0, 0, 0, 0)) {
                setDatePickerDate(todaysDate);
            }
        } else if (!Date.parse($dpInput.val())) {
            setDatePickerDate(todaysDate);
        }
    }

    $dpInput.datepicker({
        minDate: 0,
        maxDate: '+4m',
        constrainInput: true,
        defaultDate: +1,
        dayNamesMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
        onClose: function () {
            $dpInput.data('datepicker').inline = false;
        }
    });

    $dpInput.on('focusin', function () {
        $dpInput.data('previous-value', $dpInput.val());
    });

    $dpInput.on('keydown', function (event) {
        if (event.keyCode === 13) { // enter key
            $dpInput.val($dpInput.data('selected-value'));
            ensurePickerHasCurrentOrFutureDate();
            closeDP();
        }
    });

    $dpInput.on('focusout', function () {
        ensurePickerHasCurrentOrFutureDate();
    });
}

module.exports = {
    init: function () {
        setupDatepicker();
    },

    /**
     * Input for gift custom amount
     */
    handleCustomValue: function () {
        $('[name$="_giftCardCustomAmount"]').focus(function (e) {
            var $swatchClicked = $(e.currentTarget);
            var $productContainer = $swatchClicked.closest('.product-detail');
            var customValueUrl = $swatchClicked.closest('[data-attr-value="CUSTOM"]').data('attr-url');
            attributeSelect(customValueUrl, $productContainer);
        });
    },

    /**
     * When custom amount is removed, the first tile amount should be pre-selected.
     */
    handleCustomValueBlur: function () {
        $('[name$="_giftCardCustomAmount"]').on('blur', function () {
            var customAmount = $(this).val();
            if (window.digitalData && window.digitalData.page && window.digitalData.page.product && window.digitalData.page.product.productInfo) {
                window.digitalData.page.product.productInfo.giftCardValue = customAmount;
                window.digitalData.page.product.productInfo.price = customAmount;
            }
            if (customAmount === '') {
                clientSideValidation.functions.clearFieldInvalidState($('#giftCardCustomAmount')[0]);
                $('[data-attr="giftCardValueVariation"] button').first().trigger('click');
            }
        });
    },

    /**
     * Gift message character countdown
     */
    giftMessageCount: function () {
        // Regex pattern will look for specific invalid characters or any character outside of the range U+0020 - U+00ff, U+2019 (right single quote)
        var invalidChars = /[#*>^`~]|[^\u0020-\u00ff\u2019]/g;
        var textMax = 200;
        var textLength;

        $('#giftMessage').on('input change paste', function (e) {
            if (e.type === 'paste') {
                var paste = (e.originalEvent.clipboardData || window.clipboardData).getData('text');
                paste = paste.replace(invalidChars, ''); // Remove any invalid characters

                // Override the default behavior using event.preventDefault(),
                // then insert modified string manually.
                e.preventDefault();
                utils.insertAtInputCursor(this, paste);
            } else {
                $(this).val($('#giftMessage').val().replace(invalidChars, ''));
            }

            $(this).val($('#giftMessage').val().slice(0, textMax));
            textLength = $('#giftMessage').val().length;

            var textRemaining = textMax - textLength;
            var $countMessage = $('.count-message');
            var $countNumber = $('.count-message__number');

            $countNumber.html(textRemaining);
            $countMessage.toggleClass('exceeded', !textRemaining).toggleClass('active', !!textRemaining); // ! and !! to coerce the int to booleans

            return true;
        });
    },

    updateAddToCartFormData: function () {
        $('body').on('product:beforeAddToCart', function (evt, element) {
            var $container = $(element).closest('.product-detail');
            var fieldsToProcess = getSelectedGiftCardType($container) === 'virtual' ? virtualCardFields : physicalCardFields;

            $(element).on('updateAddToCartFormData', function (e, formObj) {
                var gcdetailsFormData = getFieldsData($container, fieldsToProcess);
                $.extend(formObj, gcdetailsFormData);
            });
        });
    },

    handleFormValidation: function () {
        $('body').on('product:afterAddToCart', function (e, data) {
            if (!data.success) {
                formValidation('form.gcdetails', data);
            } else {
                $('#giftMessage')[0].form.reset();
                // select the first value variation
                var $firstValueVariation = $('[data-attr="giftCardValueVariation"] button').first();
                $firstValueVariation.addClass('selected');
                attributeSelect($firstValueVariation.data('attr-url'), $firstValueVariation.closest('.product-detail'));
                // reset the characters remaining count
                $('#giftMessage').trigger('change');
            }
        });
    },

    handleAddOrUpdateCardToCart: function () {
        $(document).on('click', 'button.add-card-to-cart, button.update-card-to-cart', function () {
            var $container = $(this).closest('.product-detail');
            var fieldsToProcess = getSelectedGiftCardType($container) === 'virtual' ? virtualCardFields : physicalCardFields;

            var isFormValid = getFieldsValidationStatus($container, fieldsToProcess);
            if (isFormValid) {
                if ($(this).hasClass('add-card-to-cart')) {
                    $(this).parent().find('button.add-to-cart').trigger('click');
                } else {
                    $('body').trigger('giftcard:updateCart');
                }
            } else {
                $container.find('form.gcdetails').trigger('submit');
            }
        });
    },

    updateCardAttributesForm: function () {
        $('body').on('product:afterAttributeSelect', function (e, response) {
            updateCardAttributesFormClass(response.container);
        });
        $('.product-detail').each(function () {
            updateCardAttributesFormClass($(this));
        });
    },

    selectAttribute: function () {
        $(document).on('click', '[data-attr="giftCardTypeVariation"] button, [data-attr="giftCardDesignVariation"] button, [data-attr="giftCardValueVariation"] button', function (e) {
            e.preventDefault();
            var $swatchClicked = $(e.currentTarget);
            if ($swatchClicked.hasClass('disabled')) {
                return;
            }
            var $productContainer = $swatchClicked.closest('.set-item');
            if (!$productContainer.length) {
                $productContainer = $swatchClicked.closest('.product-detail');
            }

            $swatchClicked.off('mouseleave');
            attributeSelect($swatchClicked.data('attr-url'), $productContainer);
        });
    },

    updateToCart: function () {
        $('body').on('giftcard:updateCart', function () {
            $('body').trigger('product:beforeAddToCart', $('button.update-card-to-cart').get('0'));

            var updateProductUrl = $('.update-cart-url').val();
            var uuid = $('input[name$=_giftCardPliUUID]').val();
            var pid = $('.product-detail:not(".bundle-item")').data('pid');
            var form = {
                uuid: uuid,
                pid: pid,
                quantity: 1
            };

            $('button.update-card-to-cart').trigger('updateAddToCartFormData', form);

            global.quickview.startSpinner();

            $.ajax({
                url: updateProductUrl,
                type: 'post',
                context: this,
                data: form,
                dataType: 'json',
                success: function (data) {
                    $('body').trigger('product:afterAddToCart', data);
                    $.spinner().stop();
                    if (data.success) {
                        window.location = data.redirectUrl;
                    }
                },
                error: function () {
                    $.spinner().stop();
                }
            });
        });
    },
    /**
    * Gift card Custom amount length restrict to 3 digits
    * to compatible with the older versions of browsers like mozilla and IE10, IE11 we need to check the charachter code aswell
    */
    giftCardCustomAmountLength: function () {
        $('#giftCardCustomAmount').keypress(function (evt) {
            var charCode = (evt.which) ? evt.which : evt.keyCode;
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) || this.value.length >= 3) {
                return false;
            }
            return true;
        });
    },
    giftCardInspirationModal: function () {
        $('body')
            .off('click', '.js-giftcard-inspiration-link')
            .on('click', '.js-giftcard-inspiration-link', () => {
                const modalContent = $('.js-giftcard-inspiration-asset').html();
                if (modalContent.length) {
                    window.gcInspoModal = $.modal({
                        content: modalContent,
                        modalSizeClass: 'modal-xl modal-pad-fix js-giftcard-inspiration-modal'
                    });
                }
            });

        $('body')
            .off('click', '.js-giftcard-inspiration-modal a')
            .on('click', '.js-giftcard-inspiration-modal a', (e) => {
                e.preventDefault();
                const attrValue = $(e.currentTarget).attr('href').replace(/\D/g, '');
                const attrSelector = `[data-attr-value=${attrValue}]`;
                const $attrElement = $(attrSelector);

                if ($attrElement.length) {
                    $attrElement.parent().trigger('click');
                }

                window.gcInspoModal.modal('hide');
            });
    }
};
